import { isAfter, isSameDay, parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { getDateInTimezone, parseDay, timeZone } from './date-time';
/**
 * @param timeslotStartTime - Start time of the timeslot in ISO format.
 * @param now - Current time in ISO format.
 * @returns true/false depending on if the timeslot is in the future or not.
 */
export const isTimeslotInTheFuture = (timeslotStartTime, now) => {
    const startTime = parseISO(timeslotStartTime);
    return isAfter(startTime, now);
};
/**
 *  Used to determine if a given timeslot is in the same day as the current date.
 * @param timeslotStartTime - Timeslot start time in ISO format
 * @param compareTo - Date to compare to
 * @returns
 */
export const isTimeSlotInSelectedDay = (timeslotStartTime, compareTo) => {
    const startTime = parseISO(timeslotStartTime);
    return isSameDay(startTime, compareTo);
};
/*
 * Get unique days from timeSlots and then check which days are available
 */
export function getUniqueDayList(timeSlots) {
    const uniqueDays = timeSlots.reduce((acc, { startTime, isAvailable }) => {
        const splitDay = startTime.split('T')[0];
        if (acc[splitDay] === undefined) {
            return { ...acc, [splitDay]: isAvailable };
        }
        else if (!acc[splitDay] && isAvailable) {
            return { ...acc, [splitDay]: isAvailable };
        }
        return acc;
    }, {});
    return Object.entries(uniqueDays).sort((a, b) => new Date(a[0]).getTime() - new Date(b[0]).getTime());
}
export const getPractitionerTimeSlots = (id, timeSlots) => timeSlots.filter((timeSlot) => {
    return timeSlot.availableSpotsByPractitionerIds[id] > 0;
});
export const getAvailableTimeSlotOnSelectedDay = ({ selectedDay, timeSlots, practitionerId, }) => timeSlots?.filter(({ startTime, availableSpotsByPractitionerIds, isAvailable }) => {
    const parsedISO = parseISO(startTime);
    /* Check if the startTime is the same as the selectedDay
     * and if the practitioner is available
     */
    const availableTimeSlots = isSameDay(parsedISO, parseDay(selectedDay)) &&
        availableSpotsByPractitionerIds[practitionerId] > 0 &&
        isAvailable;
    return availableTimeSlots;
});
/*
 * Finds the next available time slot for a practitioner
 */
export const findNextAvailableTimeSlotByPractitioner = (practitionerTimeslots, selectedDay, practitionerId) => {
    const timeSlotsOnSelectedDay = getAvailableTimeSlotOnSelectedDay({
        selectedDay,
        timeSlots: practitionerTimeslots,
        practitionerId,
    });
    if (timeSlotsOnSelectedDay?.length > 0) {
        return timeSlotsOnSelectedDay[0];
    }
    return findNextAvailableTimeSlot(practitionerTimeslots, selectedDay);
};
/**
 * Get timeslots for a practitioner on a specific day
 *
 * @param {TimeSlot[]} timeSlots
 * @param {Date} selectedDate
 * @param {Practitioner[]} practitioners
 */
export const getPractitionersWithSortedTimeSlotsForSelectedDate = ({ timeSlots, selectedDate, practitioners, }) => {
    return practitioners.map((practitioner) => {
        const practitionerTimeSlots = getPractitionerTimeSlots(practitioner.id, timeSlots);
        const now = new Date();
        const timeSlotsInSelectedDateThatHaveNotPassed = practitionerTimeSlots.filter(({ startTime }) => {
            const parsedISO = parseISO(startTime);
            const startTimeInFuture = parsedISO > now;
            return (getDateInTimezone(parsedISO) === selectedDate && startTimeInFuture);
        });
        /*
         * If there are no available time slots on the selected day,
         * we want to show the next available time slot
         */
        const nextAvailableTimeSlot = findNextAvailableTimeSlotByPractitioner(practitionerTimeSlots, selectedDate, practitioner.id);
        return {
            ...practitioner,
            timeSlots: timeSlotsInSelectedDateThatHaveNotPassed,
            nextAvailableTimeSlotInDaysAhead: nextAvailableTimeSlot,
        };
    });
};
export const findNextAvailableTimeSlot = (timeSlots, selectedDay) => timeSlots.find(({ startTime, isAvailable }) => {
    const parsedISO = parseISO(startTime);
    const startTimeAfterSelectedDate = parsedISO > parseDay(selectedDay);
    return startTimeAfterSelectedDate && isAvailable;
});
export const getSortedHours = (timeSlots) => timeSlots.reduce((acc, timeSlot) => {
    const startHour = parseInt(formatInTimeZone(new Date(timeSlot.startTime), timeZone, 'HH'));
    const isMorning = startHour < 12;
    const isAfternoon = startHour >= 12 && startHour < 18;
    if (isMorning) {
        return { ...acc, morning: [...acc.morning, timeSlot] };
    }
    else if (isAfternoon) {
        return { ...acc, afternoon: [...acc.afternoon, timeSlot] };
    }
    else {
        return { ...acc, evening: [...acc.evening, timeSlot] };
    }
}, { morning: [], afternoon: [], evening: [] });
